interface Props {
  src?: string | null;
  name: string;
  size?: number;
  className?: string;
}

/**
 * A user's picture, or their initials.
 *
 * The fallback is not decoration. Most accounts will never upload a picture, so
 * no-avatar is the common case, and a page of identical grey silhouettes is
 * genuinely harder to scan than a page of coloured initials.
 *
 * The colour is derived from the name, so it is stable: the same person is the
 * same colour on every page and every session, without storing anything.
 */
export function Avatar({ src, name, size = 40, className = '' }: Props) {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase();

  // Hues drawn from the brand's own range - indigo through teal to green -
  // rather than the full wheel, which lands on colours that fight the palette
  // and fail contrast against the near-black ground.
  const hues = [243, 262, 210, 160, 140, 285];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  const hue = hues[Math.abs(hash) % hues.length];

  if (src) {
    return (
      <img
        src={src}
        // Empty alt, deliberately: the name is always rendered beside the
        // avatar, so describing it again makes a screen reader say it twice.
        alt=""
        width={size}
        height={size}
        className={`shrink-0 rounded-full object-cover ${className}`}
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <div
      aria-hidden
      className={`flex shrink-0 items-center justify-center rounded-full font-medium ${className}`}
      style={{
        width: size,
        height: size,
        fontSize: size * 0.36,
        background: `hsl(${hue} 45% 20%)`,
        color: `hsl(${hue} 70% 76%)`,
      }}
    >
      {initials || '?'}
    </div>
  );
}
